import { useEffect, useState } from "react";
import AuthLayout from "../../layouts/AuthLayout"
import MobileView from "../../layouts/MobileView/MobileView"
import { Link, useNavigate } from "react-router-dom";
import { INIT_IG_AUTH_ROUTE, LANDING_PAGE_URL } from "../../constants/constants";
import VerifyOTP from "../../components/login/VerifyOTP";
import { useDispatch } from "react-redux";
import { AppDispatch, getIntendedPath, RootState } from "../../redux-stores/store";
import { useSelector } from "react-redux";
import { fetchInfluencer } from "../../redux-stores/slices/influencerSlice";
import { login } from "../../redux-stores/slices/authSlice";
import { generateOTP, registerUser } from "../../services/api/auth";
import { analyticsSetUser, analyticsLog } from "../../lib/analytics";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../utils/helpers";
import AuthPhoneInput from "../../components/login/AuthPhoneInput";
import { useAuthPhoneCountries } from "../../hooks/useAuthPhoneCountries";
import { AuthPhoneValue, EMPTY_AUTH_PHONE } from "../../types/auth";
import { formatAuthPhoneDisplay, isValidAuthPhone } from "../../utils/phone";

interface ValidationResults {
    name?: null | { status: boolean, message?: string },
    number?: null | { status: boolean, message?: string }
}

const Register: React.FC = () => {
    const [phone, setPhone] = useState('');
    const [phoneValue, setPhoneValue] = useState<AuthPhoneValue>(EMPTY_AUTH_PHONE);
    const [name, setName] = useState<string>('');
    const [otpSent, setOTPSent] = useState<boolean>(false);
    const [mode, setMode] = useState<string>('');

    const [validating, setValidating] = useState<boolean>(false);

    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { isAuthenticated } = useSelector((state: RootState) => state.auth);
    const connectionRequired = useSelector((state: RootState) => state.influencer.connectionRequired);
    const igAuthWall = useSelector((state: RootState) => state.auth.configurations?.ig_auth_wall === 'y')
    const isVerified = useSelector((state: RootState) => state.influencer.influencer?.verification?.is_verified);
    const { countries, defaultCountry } = useAuthPhoneCountries();

    const [validationResults, setValidationResults] = useState<ValidationResults>({});

    const fetchUser = async (token: string) => {
        await dispatch(fetchInfluencer(token));
        const data = (await dispatch(login(token)).unwrap()).userData;
        return data;
    }

    useEffect(() => {
        if (isAuthenticated) {
            const redirectURL = (connectionRequired && igAuthWall && !isVerified) ? INIT_IG_AUTH_ROUTE : getIntendedPath();
            navigate(redirectURL);
        }
    }, [isAuthenticated, connectionRequired, navigate])

    const validate = () => {
        validateName(name);
        validateNumber(phoneValue);
    
        return (
            name.length >= 2 &&
            isValidAuthPhone(phoneValue)
        );
    };

    const validateName = (value: string) => {
        setValidationResults(prev => {
            const updated = { ...prev };

            if (!value) {
                updated.name = { status: false, message: "Please enter a valid name" };
            } else if (value.length < 2) {
                updated.name = { status: false, message: "Name should contain minimum 2 characters" };
            } else {
                delete updated.name;
            }

            return updated;
        });
    };

    const validateNumber = (value: AuthPhoneValue) => {
        setValidationResults(prev => {
            const updated = { ...prev };

            if (!isValidAuthPhone(value)) {
                updated.number = { status: false, message: "Please enter a valid mobile number" };
            } else {
                delete updated.number;
            }

            return updated;
        });
    };

    const sendOTP = async () => {
        if (!validate()) return;

        try {
            setValidating(true);
            const res = await generateOTP({
                mode: 'mobile',
                username: phoneValue.username,
                country: phoneValue.country,
            });
            if (res.status) {
                setOTPSent(true);
                setMode(res.mode!);
            }
            return res;
        } catch (error) {
            console.error(error)
            toast.error(getErrorMessage(error));
        } finally {
            setValidating(false);
        }
    }

    const register = async (otp: string) => {
        try {
            let referrer = null;
            if (window.localStorage.getItem("_tt_referrer")) {
                referrer = window.localStorage.getItem("_tt_referrer");
            }
            const result = await registerUser({
                country: phoneValue.country,
                country_code: phoneValue.country_code,
                mobile: phoneValue.mobile,
                otp,
                name,
                referrer,
            });
            if (!result.token) throw new Error("Something went wrong");
            const userData = await fetchUser(result.token)
            if (userData && result.is_new) {
                await analyticsSetUser(userData.user.uuid);
                analyticsLog('sign_up', {
                    name: userData.user.name,
                    uuid: userData.user.uuid,
                })
                navigate('/registration-success');
            }
        } catch (error) {
            console.error(error)
            throw error;
        }
    }


    return (
        <MobileView>
            <AuthLayout>
                {
                    !otpSent ? (
                        <div className="position-relative h-100 auth-content-inner">
                            <h4 className="w-100 text-center auth-heading">Create Your Account</h4>
                            <div className="auth-form">
                                <div>
                                    <input type="text"
                                        className="form-control validate-name auth-form-input"
                                        id="form1"
                                        placeholder="Enter Your Name"
                                        value={name}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setName(value);
                                            validateName(value);
                                        }}
                                    />
                                    {
                                        !!validationResults.name?.message &&
                                        <span className="text-danger">{validationResults.name.message}</span>
                                    }
                                </div>
                                <div>
                                    <AuthPhoneInput
                                        value={phone}
                                        defaultCountry={defaultCountry}
                                        countries={countries}
                                        onChange={(nextPhone, fields) => {
                                            setPhone(nextPhone);
                                            setPhoneValue(fields);
                                            if (fields.mobile) {
                                                validateNumber(fields);
                                            } else {
                                                setValidationResults(prev => {
                                                    const next = { ...prev };
                                                    delete next.number;
                                                    return next;
                                                });
                                            }
                                        }}
                                        onEnter={sendOTP}
                                    />
                                    {
                                        !!validationResults.number?.message &&
                                        <span className="text-danger">{validationResults.number.message}</span>
                                    }
                                </div>

                                <button
                                    onClick={sendOTP}
                                    disabled={
                                        validating ||
                                        !name ||
                                        !isValidAuthPhone(phoneValue) ||
                                        Object.keys(validationResults).length > 0
                                    } 
                                    className="btn btn-primary">Continue</button>
                            </div>
                            <div className="d-flex flex-column justify-content-between h-100 gap-5">
                                <div className="auth-nav-content">
                                    <span>Already have an account? <Link to={{ pathname: '/login' }} className="text-primary fw-600"> Log In </Link></span>
                                </div>
                                <div className="text-center">
                                    <span>
                                        By continuing, you agree to our <a href={LANDING_PAGE_URL + '/legal/privacy.html'} className="text-primary fw-600" target="_blank" rel="noreferrer"> Privacy Policy </a> and <a href={LANDING_PAGE_URL + '/legal/terms.html'} className="text-primary fw-600" target="_blank" rel="noreferrer">Terms of Use</a>
                                    </span>
                                </div>
                            </div>

                        </div>
                    ) : (
                        <div className="position-relative h-100">
                            <VerifyOTP
                                mode={mode}
                                displayNumber={formatAuthPhoneDisplay(phoneValue)}
                                onResend={sendOTP}
                                onVerify={register}
                                onBackButtonClick={() => setOTPSent(false)}
                            />
                        </div>
                    )
                }
            </AuthLayout>
        </MobileView>

    )
}

export default Register
